import { useCallback, useEffect, useState } from 'react'
import type { AuthUser } from '../../shared/types'

async function authRequest(
  path: string,
  options?: RequestInit,
): Promise<{ user?: AuthUser | null; error?: string; devCode?: string }> {
  const response = await fetch(path, {
    ...options,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(options?.headers ?? {}) },
  })
  if (response.status === 204) return {}
  const body = (await response.json().catch(() => ({}))) as { user?: AuthUser | null; error?: string }
  if (!response.ok) {
    if (response.status === 429) throw new Error('Too many attempts. Wait a few minutes.')
    throw new Error(body.error || 'Something went wrong.')
  }
  return body
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    void authRequest('/api/auth/me')
      .then((body) => setUser(body.user ?? null))
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  const requestCode = useCallback(async (email: string, turnstileToken?: string) => {
    const body = await authRequest('/api/auth/code/request', {
      method: 'POST',
      body: JSON.stringify({ email, ...(turnstileToken ? { turnstileToken } : {}) }),
    })
    return body.devCode
  }, [])

  const verifyCode = useCallback(async (email: string, code: string) => {
    const body = await authRequest('/api/auth/code/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    })
    setUser(body.user ?? null)
  }, [])

  const logout = useCallback(async () => {
    await authRequest('/api/auth/logout', { method: 'POST' })
    setUser(null)
  }, [])

  const loginWithGoogle = useCallback(async (credential: string) => {
    const body = await authRequest('/api/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    })
    setUser(body.user ?? null)
  }, [])

  const updateProfile = useCallback(async (profile: Partial<Omit<AuthUser, 'id' | 'email' | 'color'>> & Pick<AuthUser, 'name' | 'role'>) => {
    const body = await authRequest('/api/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(profile),
    })
    if (body.user) setUser(body.user)
    return body.user ?? null
  }, [])

  const uploadAvatar = useCallback(async (file: File) => {
    const response = await fetch('/api/auth/avatar', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': file.type },
      body: file,
    })
    const body = (await response.json()) as { user?: AuthUser; error?: string }
    if (!response.ok || !body.user) throw new Error(body.error || 'Upload failed.')
    setUser(body.user)
    return body.user
  }, [])

  return { user, loading, requestCode, verifyCode, loginWithGoogle, logout, updateProfile, uploadAvatar }
}
